import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Download04, LinkExternal01 } from '@untitled-ui/icons-react'
import { MyButton, MyDetailView, myToaster } from '@interstellar-component'
import Overlay from '@src/components/Overlay'
import { useMediaBlob, useObjectUrl } from '@src/components/useMedia'
import { fetchMediaBlob, openBlob, openMedia, safeTypedBlob, saveBlob } from '@src/services/media'
import { formatFileSize } from '@src/services/Helper'
import { FILE_KIND_LABEL } from '@src/services/consentSettings'
import ENQUIRY_CATEGORY_LABEL from '@src/services/enquiryCategory'
import { fileFamily } from '../fileMatcher'
import { downloadNameOf } from '../constants'

const PDF = 'application/pdf'
const isPdfName = (name) => /\.pdf$/i.test(String(name || ''))

// How a saved file of a row (files[kind] of the rows API) is shown: a photo in
// an <img>, a PDF in an <iframe> (typed as PDF, whatever the proxy sent), or
// neither (a Word file media-service could not convert: a download).
function savedShapeOf(saved, kind) {
  if (!saved?.url) return null
  if (kind === 'ktp_photo' || /^image\//i.test(saved.mime_type || '')) return 'photo'
  if (saved.mime_type === PDF || isPdfName(saved.file_name)) return 'pdf'
  return 'other'
}

const Message = ({ children }) => (
  <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-center text-sm text-gray-500">
    {children}
  </div>
)
Message.propTypes = { children: PropTypes.node }

/** A file picked in this browser, not saved yet: images and PDFs from an object URL. */
export function LocalFileView({ file }) {
  const family = file ? fileFamily(file).family : null
  const pdf = family === 'document' && isPdfName(file?.name)
  // A PDF is shown typed as one, whatever type the browser gave the file.
  const shown = useMemo(() => {
    if (family === 'photo') return file
    return pdf ? safeTypedBlob(file, PDF) : null
  }, [file, family, pdf])
  const url = useObjectUrl(shown)
  if (!file) return <Message>No file.</Message>
  if (family === 'photo') {
    return url ? <img src={url} alt={file.name} className="max-h-full max-w-full self-center object-contain" /> : null
  }
  if (pdf) return url ? <iframe src={url} title={file.name} className="h-full min-h-[60vh] w-full border-0" /> : null
  return <Message>Word files can be previewed once saved: the server turns them into PDF.</Message>
}
LocalFileView.propTypes = { file: PropTypes.object }

// A saved photo or PDF as FilePreview loaded it (`media`: { status, url, reload }).
function SavedFileView({ saved, kind, shape, media }) {
  const label = saved?.file_name || FILE_KIND_LABEL[kind]
  if (!shape) return <Message>No file to preview.</Message>
  if (shape === 'other') return <Message>This file cannot be shown here. Download it to open it.</Message>
  const what = shape === 'photo' ? 'photo' : 'document'
  if (media.status === 'error') {
    return (
      <Message>
        <span>The {what} could not be loaded.</span>
        <MyButton type="button" color="secondary" variant="outlined" size="sm" onClick={media.reload}>
          Try again
        </MyButton>
      </Message>
    )
  }
  if (!media.url) return <Message>Loading the {what}…</Message>
  if (shape === 'photo') {
    return <img src={media.url} alt={label} className="max-h-full max-w-full self-center object-contain" />
  }
  return <iframe src={media.url} title={label} className="h-full min-h-[60vh] w-full border-0" />
}
SavedFileView.propTypes = {
  saved: PropTypes.object,
  kind: PropTypes.string,
  shape: PropTypes.oneOf(['photo', 'pdf', 'other']),
  media: PropTypes.shape({ status: PropTypes.string, url: PropTypes.string, reload: PropTypes.func }).isRequired,
}

const GENDER_LABEL = { male: 'Male', female: 'Female' }
// 'YYYY-MM-DD' as the e-KTP card prints it (DD-MM-YYYY).
const cardDate = (value) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''))
  return m ? `${m[3]}-${m[2]}-${m[1]}` : value || '—'
}

/** The row's person, and the e-KTP data the workbook gave (or the one on file it reuses). */
export function RowIdentity({ row }) {
  if (!row) return null
  const ktp = row.ktp
  const person = {
    Row: row.row_no,
    Category: ENQUIRY_CATEGORY_LABEL[row.category] || row.category || '—',
    Name: row.full_name || '—',
    Email: row.email || '—',
  }
  if (row.employee_code) person['Employee ID'] = row.employee_code
  const datas = ktp
    ? {
        NIK: row.nik || '—',
        'Name on e-KTP': ktp.name || '—',
        'Date of birth': cardDate(ktp.dob),
        Gender: GENDER_LABEL[ktp.gender] || ktp.gender || '—',
        Address: ktp.address || '—',
        City: ktp.city || '—',
        District: ktp.district || '—',
        Village: ktp.village || '—',
        'Postal code': ktp.postal_code || '—',
      }
    : null
  return (
    <div className="flex flex-col gap-3">
      <MyDetailView header="Row" datas={person} />
      {datas && (
        <MyDetailView
          header={row.ktp_source === 'reuse' ? 'e-KTP on file (reused)' : 'e-KTP data from the workbook'}
          datas={datas}
        />
      )}
    </div>
  )
}
RowIdentity.propTypes = { row: PropTypes.object }

/**
 * Preview of one file of a row: a file picked in this browser (`file`), or a
 * saved one (`saved` = files[kind] of the rows API, fetched with the Bearer,
 * never a plain <img src>). `row` adds the person and e-KTP data beside it, to
 * compare with the photo. Neither file nor saved: the row's data alone.
 *
 * A saved file is only ever rendered as what its row says it is: a photo in an
 * <img>, a document as a PDF (the blob is re-typed, so a file whose stored name
 * says .svg or .html runs no script on this origin), and a download is named
 * with the extension of its allow-listed type (downloadNameOf).
 */
export default function FilePreview({ open, onClose, kind, file, saved, row }) {
  const [downloading, setDownloading] = useState(false)
  const shape = file ? null : savedShapeOf(saved, kind)
  // The saved photo or PDF, read while the preview is open.
  const loaded = useMediaBlob(open && (shape === 'photo' || shape === 'pdf') ? saved.url : null)
  const shownBlob = useMemo(() => {
    if (!loaded.blob) return null
    return shape === 'pdf' ? safeTypedBlob(loaded.blob, PDF) : loaded.blob
  }, [loaded.blob, shape])
  const objectUrl = useObjectUrl(shownBlob)

  const name = file?.name || saved?.file_name || FILE_KIND_LABEL[kind] || 'File'
  const size = file?.size ?? saved?.file_size
  const downloadName = downloadNameOf(saved?.file_name, saved?.mime_type)

  const openInTab = () => {
    if (shownBlob) {
      openBlob(shownBlob, { type: PDF, fileName: downloadName })
      return
    }
    openMedia(saved.url, { type: PDF, fileName: downloadName }).catch(() =>
      myToaster({ status: 400, message: 'The file could not be opened.' })
    )
  }

  const download = async () => {
    setDownloading(true)
    try {
      saveBlob(await fetchMediaBlob(saved.url), downloadName)
    } catch {
      myToaster({ status: 400, message: 'The file could not be downloaded.' })
    } finally {
      setDownloading(false)
    }
  }

  if (!file && !saved) {
    return (
      <Overlay open={open} onClose={onClose} title={row ? `Row ${row.row_no} · e-KTP data` : 'e-KTP data'} maxWidth={480}>
        <div className="p-4">
          <RowIdentity row={row} />
        </div>
      </Overlay>
    )
  }

  const kindLabel = FILE_KIND_LABEL[kind] || 'File'
  return (
    <Overlay
      open={open}
      onClose={onClose}
      title={row ? `Row ${row.row_no} · ${kindLabel}` : kindLabel}
      subtitle={
        <span className="break-all">
          {name}
          {size ? ` · ${formatFileSize(size)}` : ''}
          {file ? ' · not saved yet' : ''}
        </span>
      }
      actions={
        <>
          {shape === 'pdf' && (
            <button
              type="button"
              title="Open in a new tab"
              aria-label="Open in a new tab"
              onClick={openInTab}
              className="rounded-md p-1.5 text-gray-500 hover:bg-gray-50 hover:text-gray-900"
            >
              <LinkExternal01 className="size-5" stroke="currentColor" />
            </button>
          )}
          {shape === 'other' && (
            <MyButton
              type="button"
              color="secondary"
              variant="outlined"
              size="sm"
              customClassname="gap-1.5"
              disabled={downloading}
              onClick={download}
            >
              <Download04 className="size-4" stroke="currentColor" /> Download
            </MyButton>
          )}
        </>
      }
    >
      <div className="flex min-h-[60vh] flex-1 flex-col lg:flex-row">
        <div className="flex min-h-[60vh] flex-1 flex-col items-stretch justify-center overflow-auto bg-gray-50 p-3">
          {file ? (
            <LocalFileView file={file} />
          ) : (
            <SavedFileView
              saved={saved}
              kind={kind}
              shape={shape}
              media={{ status: loaded.status, url: objectUrl, reload: loaded.reload }}
            />
          )}
        </div>
        {row && (
          <div className="w-full shrink-0 overflow-auto border-t border-gray-200 p-4 lg:w-[320px] lg:border-l lg:border-t-0">
            <RowIdentity row={row} />
          </div>
        )}
      </div>
    </Overlay>
  )
}

FilePreview.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  kind: PropTypes.string,
  file: PropTypes.object,
  saved: PropTypes.shape({
    url: PropTypes.string,
    mime_type: PropTypes.string,
    file_name: PropTypes.string,
    file_size: PropTypes.number,
  }),
  row: PropTypes.object,
}
